import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { STAGED_FOLDER_NAME_PATTERN } from "@quovy/types";
import {
  Bot,
  ChevronLeft,
  ChevronRight,
  Columns3,
  FileText,
  FolderInput,
  Key,
  Plug,
  Plus,
  Puzzle,
  Table2,
  Wrench,
} from "lucide-react";
import { cn } from "../../utils";
import type { ChipListHandle } from "../rich-chip-editor/chip-suggestion";
import {
  SuggestionListContainer,
  SuggestionRow,
  useSuggestionListNav,
} from "../rich-chip-editor/suggestion-list";
import { humanizeStagedFolderName, sanitizeStagedFolderName } from "./staged-folder-names";
import {
  ENTITY_TAGGABLE_TYPES,
  type EntityTaggableType,
  type EntityRefType,
} from "@quovy/types/chip-doc/entity-refs";
import type { EntityTaggable } from "./types";

export interface EntityRefListProps {
  items: EntityTaggable[];
  command: (attrs: {
    id: string;
    label: string;
    refType: EntityRefType;
    /** Field key when the chosen row is a custom-model FIELD. */
    field?: string;
  }) => void;
  /** Current `@` query. Controls whether the "Add integration" action stays
   *  visible when nothing is configured yet (only on the bare, empty query). */
  query?: string;
  /** When set, an "Add integration" action is shown at the bottom of the
   *  Integrations group; selecting it opens this URL in a new tab. */
  addIntegrationHref?: string;
  /** Invoked when the "Add integration" action is chosen. The suggestion layer
   *  passes this to first dismiss the `@` session (clearing the partial trigger
   *  text) and then open {@link addIntegrationHref}. When omitted, the action
   *  falls back to opening the href directly. */
  onAddIntegration?: () => void;
  /** When set, a query that slugs to a valid, undeclared staged folder name
   *  grows a "Create staged folder" row in the Staged folders group. The
   *  callback declares the folder (adds the row to the draft config); the
   *  list then inserts the chip via {@link command}. */
  onCreateStagedFolder?: (slug: string) => void;
  /**
   * Drill from a custom model into its fields: rewrite the `@` trigger to
   * `@<name>.` so the SAME suggestion session re-queries in field mode.
   * Invoked by ArrowRight (or the chevron) on a drillable model row — Enter
   * on that row still inserts a whole-model chip.
   */
  onDrillCustomModel?: (modelName: string) => void;
  /** Whether the caret sits at the end of {@link query}. ArrowRight only
   *  drills into a model's fields when true — otherwise the author is
   *  editing mid-query and the key must move the caret, not rewrite it. */
  atQueryEnd?: boolean;
  /** Rewrite the `@` query without stealing focus from the in-type search field. */
  onQueryChange?: (query: string) => void;
  /** Return caret to the editor (after leaving the type search field). */
  onFocusEditor?: () => void;
}

export interface EntityRefListHandle {
  onKeyDown: (event: KeyboardEvent) => boolean;
}

const TYPE_ICON: Record<EntityTaggableType, typeof Wrench> = {
  staged_folder: FolderInput,
  agent: Bot,
  tool: Wrench,
  file: FileText,
  custom_model: Table2,
  credential: Key,
  skill: Puzzle,
  integration: Plug,
};

/** Section header shown above each group of taggables in the `@` popup. */
const TYPE_LABEL: Record<EntityTaggableType, string> = {
  staged_folder: "Staged folders",
  agent: "Agents",
  tool: "Tools",
  file: "Files",
  custom_model: "Custom models",
  credential: "Credentials",
  skill: "Skills",
  integration: "Integrations",
};

/** A selectable row in the popup: a taggable entity, a type to drill, or an action. */
type Selectable =
  | { kind: "type"; type: EntityTaggableType }
  | { kind: "item"; item: EntityTaggable }
  | { kind: "add-integration" }
  | { kind: "create-staged-folder" };

type Group = {
  key: string;
  type: EntityTaggableType | null;
  label: string;
  items: EntityTaggable[];
  addAction: boolean;
  createStagedFolder: boolean;
};

export const EntityRefList = forwardRef<EntityRefListHandle, EntityRefListProps>(
  (
    {
      items,
      command,
      query,
      addIntegrationHref,
      onAddIntegration,
      onCreateStagedFolder,
      onDrillCustomModel,
      atQueryEnd,
      onQueryChange,
      onFocusEditor,
    },
    ref,
  ) => {
    const [activeType, setActiveType] = useState<EntityTaggableType | null>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const navRef = useRef<ChipListHandle>(null);
    const trimmedQuery = query?.trim() ?? "";
    const typeFilter = trimmedQuery.toLowerCase();

    const hasIntegrationItems = useMemo(
      () => items.some((item) => item.type === "integration"),
      [items],
    );
    const fieldItems = useMemo(
      () => items.filter((item) => item.type === "custom_model" && item.field),
      [items],
    );
    const modelItems = useMemo(
      () => items.filter((item) => item.type === "custom_model" && !item.field),
      [items],
    );

    /** `@Model.field` — the create row must not slug the dotted query. */
    const isDrilling = useMemo(() => {
      const lower = trimmedQuery.toLowerCase();
      if (!lower.includes(".")) return false;
      return (
        fieldItems.length > 0 ||
        modelItems.some((model) => lower.startsWith(`${model.label.toLowerCase()}.`))
      );
    }, [trimmedQuery, modelItems, fieldItems]);

    const createStagedFolderSlug = useMemo(() => {
      if (!onCreateStagedFolder) return null;
      if (isDrilling) return null;
      const slug = sanitizeStagedFolderName(trimmedQuery);
      if (!slug || !STAGED_FOLDER_NAME_PATTERN.test(slug)) return null;
      if (items.some((item) => item.type === "staged_folder" && item.id === slug)) return null;
      return slug;
    }, [onCreateStagedFolder, trimmedQuery, items, isDrilling]);

    const showAddIntegration =
      Boolean(addIntegrationHref) && (hasIntegrationItems || !trimmedQuery);

    const groups = useMemo(() => {
      const out: Group[] = [];
      if (fieldItems.length > 0) {
        out.push({
          key: "custom_model-field",
          type: null,
          label: "Fields",
          items: fieldItems,
          addAction: false,
          createStagedFolder: false,
        });
      }
      for (const type of ENTITY_TAGGABLE_TYPES) {
        const groupItems =
          type === "custom_model" ? modelItems : items.filter((item) => item.type === type);
        const addAction = type === "integration" && showAddIntegration;
        const createStagedFolder = type === "staged_folder" && createStagedFolderSlug !== null;
        if (groupItems.length === 0 && !addAction && !createStagedFolder) continue;
        out.push({
          key: type,
          type,
          label: TYPE_LABEL[type],
          items: groupItems,
          addAction,
          createStagedFolder,
        });
      }
      return out;
    }, [items, modelItems, fieldItems, showAddIntegration, createStagedFolderSlug]);

    const typeGroups = useMemo(() => groups.filter((group) => group.type !== null), [groups]);

    const pickingType =
      !trimmedQuery && !isDrilling && activeType === null && typeGroups.length > 1;

    useEffect(() => {
      if (isDrilling) setActiveType(null);
    }, [isDrilling]);

    useEffect(() => {
      if (activeType) searchRef.current?.focus();
    }, [activeType]);

    const visibleGroups = useMemo(() => {
      if (pickingType) return [];
      const source = activeType ? groups.filter((group) => group.type === activeType) : groups;
      if (!activeType || !typeFilter) return source;
      return source.map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          const hay =
            `${item.label} ${item.id} ${item.sublabel ?? ""} ${item.field ?? ""}`.toLowerCase();
          return hay.includes(typeFilter);
        }),
      }));
    }, [pickingType, activeType, groups, typeFilter]);

    const selectables = useMemo(() => {
      const out: Selectable[] = [];
      if (pickingType) {
        for (const group of typeGroups) {
          if (group.type) out.push({ kind: "type", type: group.type });
        }
        return out;
      }
      for (const group of visibleGroups) {
        for (const item of group.items) out.push({ kind: "item", item });
        if (group.createStagedFolder) out.push({ kind: "create-staged-folder" });
        if (group.addAction) out.push({ kind: "add-integration" });
      }
      return out;
    }, [pickingType, typeGroups, visibleGroups]);

    const openAddIntegration = (): void => {
      if (onAddIntegration) {
        onAddIntegration();
        return;
      }
      if (addIntegrationHref) window.open(addIntegrationHref, "_blank", "noopener,noreferrer");
    };

    const createStagedFolder = (): void => {
      if (createStagedFolderSlug === null) return;
      onCreateStagedFolder?.(createStagedFolderSlug);
      command({
        id: createStagedFolderSlug,
        label: humanizeStagedFolderName(createStagedFolderSlug),
        refType: "staged_folder",
      });
    };

    const selectItem = (index: number): void => {
      const row = selectables[index];
      if (!row) return;
      if (row.kind === "type") {
        setActiveType(row.type);
        return;
      }
      if (row.kind === "add-integration") {
        openAddIntegration();
        return;
      }
      if (row.kind === "create-staged-folder") {
        createStagedFolder();
        return;
      }
      command({
        id: row.item.id,
        label: row.item.label,
        refType: row.item.type,
        ...(row.item.field ? { field: row.item.field } : {}),
      });
    };

    const drillableAt = (index: number): EntityTaggable | null => {
      const row = selectables[index];
      if (!row || row.kind !== "item") return null;
      const { item } = row;
      return item.type === "custom_model" && !item.field && item.drillable ? item : null;
    };

    const drill = (index: number): boolean => {
      const model = drillableAt(index);
      if (!model || !onDrillCustomModel) return false;
      onDrillCustomModel(model.label);
      return true;
    };

    const defaultIndex = useMemo(() => {
      if (pickingType || activeType) return -1;
      const first = selectables.findIndex((row) => row.kind === "item" || row.kind === "type");
      return first === -1 ? 0 : first;
    }, [selectables, pickingType, activeType]);

    const leaveType = (): void => {
      setActiveType(null);
      onQueryChange?.("");
      onFocusEditor?.();
    };

    const searchValue = query ?? "";

    const { selectedIndex, setSelectedIndex, itemRef } = useSuggestionListNav<HTMLButtonElement>(
      navRef,
      selectables.length,
      selectItem,
      selectables,
      defaultIndex,
      (event, index) => {
        if (event.key === "ArrowLeft" && activeType) {
          leaveType();
          return true;
        }
        if (event.key === "Backspace" && activeType && !searchValue) {
          leaveType();
          return true;
        }
        if (event.key === "ArrowRight") {
          const row = selectables[index];
          if (row?.kind === "type") {
            setActiveType(row.type);
            return true;
          }
          return Boolean(atQueryEnd) && drill(index);
        }
        return false;
      },
    );

    useImperativeHandle(ref, () => ({
      onKeyDown: (event) => navRef.current?.onKeyDown(event) ?? false,
    }));

    let renderIndex = 0;

    const typeHeader =
      activeType && !pickingType ? (
        <div className="flex h-8 items-center gap-1">
          <button
            type="button"
            aria-label="Back to types"
            className="flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
            onMouseDown={(e) => e.preventDefault()}
            onClick={leaveType}
          >
            <ChevronLeft className="size-4" />
          </button>
          <input
            data-1p-ignore
            ref={searchRef}
            type="text"
            value={searchValue}
            placeholder={`Search ${TYPE_LABEL[activeType].toLowerCase()}…`}
            aria-label={`Search ${TYPE_LABEL[activeType]}`}
            className="h-full min-w-0 flex-1 bg-transparent px-1.5 ps-0 text-sm outline-none placeholder:text-muted-foreground"
            onMouseDown={(e) => e.stopPropagation()}
            onChange={(e) => onQueryChange?.(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                if (searchValue) onQueryChange?.("");
                else leaveType();
                return;
              }
              if (navRef.current?.onKeyDown(e.nativeEvent)) e.preventDefault();
            }}
          />
        </div>
      ) : null;

    return (
      <SuggestionListContainer
        className="entity-ref-popup w-72"
        empty={selectables.length === 0}
        emptyMessage="No matches"
        {...(typeHeader ? { header: typeHeader } : {})}
      >
        {pickingType &&
          typeGroups.map((group) => {
            const index = renderIndex++;
            if (!group.type) return null;
            const Icon = TYPE_ICON[group.type];
            return (
              <SuggestionRow
                key={group.key}
                index={index}
                selectedIndex={selectedIndex}
                setSelectedIndex={setSelectedIndex}
                itemRef={itemRef}
                onSelect={selectItem}
              >
                <Icon className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate font-medium">{group.label}</span>
                {group.items.length > 0 ? (
                  <span className="tabular-nums text-xs text-muted-foreground">
                    {group.items.length}
                  </span>
                ) : null}
                <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
              </SuggestionRow>
            );
          })}
        {!pickingType &&
          visibleGroups.map((group) => (
            <div key={group.key} className="pb-0.5">
              {activeType ? null : (
                <div className="px-2 pt-1.5 pb-0.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-muted-foreground">
                  {group.label}
                </div>
              )}
              {group.items.map((item) => {
                const index = renderIndex++;
                const Icon = item.field ? Columns3 : TYPE_ICON[item.type];
                const canDrill = Boolean(!item.field && item.drillable && onDrillCustomModel);
                return (
                  <SuggestionRow
                    key={`${item.type}:${item.id}:${item.field ?? ""}`}
                    index={index}
                    selectedIndex={selectedIndex}
                    setSelectedIndex={setSelectedIndex}
                    itemRef={itemRef}
                    onSelect={selectItem}
                    className={cn(item.description && "items-start")}
                  >
                    <Icon
                      className={cn(
                        "size-3.5 shrink-0 text-muted-foreground",
                        item.description && "mt-1",
                      )}
                    />
                    {item.description ? (
                      <span className="min-w-0 flex-1 text-left">
                        <span className="block truncate font-medium">{item.label}</span>
                        <span className="mt-0.5 block truncate text-xs font-normal text-muted-foreground">
                          {item.description}
                        </span>
                      </span>
                    ) : (
                      <span className="truncate font-medium">{item.field ?? item.label}</span>
                    )}
                    {item.sublabel && (
                      <span className="ml-auto truncate text-xs text-muted-foreground">
                        {item.sublabel}
                      </span>
                    )}
                    {canDrill && (
                      <div
                        role="button"
                        tabIndex={-1}
                        aria-label={`Show fields of ${item.label}`}
                        title="Show fields (→)"
                        className={cn(
                          "shrink-0 rounded-sm p-0.5 text-muted-foreground hover:bg-background/60",
                          !item.sublabel && "ml-auto",
                          item.description && "mt-0.5",
                        )}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => {
                          e.stopPropagation();
                          drill(index);
                        }}
                      >
                        <ChevronRight className="size-3.5" />
                      </div>
                    )}
                  </SuggestionRow>
                );
              })}
              {group.createStagedFolder &&
                (() => {
                  const index = renderIndex++;
                  return (
                    <SuggestionRow
                      key="create-staged-folder"
                      index={index}
                      selectedIndex={selectedIndex}
                      setSelectedIndex={setSelectedIndex}
                      itemRef={itemRef}
                      onSelect={selectItem}
                      muted
                    >
                      <Plus className="size-3.5 shrink-0" />
                      <span className="truncate">
                        Create staged folder{" "}
                        <span className="font-medium text-foreground">
                          &ldquo;
                          {createStagedFolderSlug !== null
                            ? humanizeStagedFolderName(createStagedFolderSlug)
                            : ""}
                          &rdquo;
                        </span>
                      </span>
                    </SuggestionRow>
                  );
                })()}
              {group.addAction &&
                (() => {
                  const index = renderIndex++;
                  return (
                    <SuggestionRow
                      key="add-integration"
                      index={index}
                      selectedIndex={selectedIndex}
                      setSelectedIndex={setSelectedIndex}
                      itemRef={itemRef}
                      onSelect={selectItem}
                      muted
                    >
                      <Plus className="size-3.5 shrink-0" />
                      <span className="font-medium">Add integration</span>
                    </SuggestionRow>
                  );
                })()}
            </div>
          ))}
      </SuggestionListContainer>
    );
  },
);

EntityRefList.displayName = "EntityRefList";
